import axios from 'axios';
import { intersection } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useCallback, useContext, useState } from 'react';
import { useCookie } from 'react-use';

import {
  CTAInteractionTrackingEvents,
  InterestedOptions,
  TourCustomPageValue,
} from '@revelio/core';

import { environment } from '../../environment';
import { SurfContext } from '../../surf-context';
import { isE2eTestSignupEmail } from '../utils/is-e2e-test-signup-email';
import { AboutYouFields } from './about-you-fields';
import { ThemeHeading, ThemeTagline } from './theme/theme';
import { Stack } from './ui';

const interestedOptionsCusttomTourHandlingKeys = [
  InterestedOptions.Data_Enrichment_and_Custom_Reports,
  InterestedOptions.Talent_Acquisition,
];

const isLocalhost = window.location.host.startsWith('localhost');

function Step2A({
  setActiveStep,
  formData,
}: {
  setActiveStep: (step: number) => void;
  formData: { name?: string; email: string };
}) {
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState('');
  const [interest, setInterest] = useState('');
  const csrfToken = useContext(SurfContext);

  const [, updateCookie] = useCookie(TourCustomPageValue);

  const handleOnboarding = useCallback(async () => {
    setLoading(true);
    try {
      if (formData.email) {
        const interestedIntersection = intersection<string>(
          interestedOptionsCusttomTourHandlingKeys,
          [interest]
        );
        if (interestedIntersection.length) {
          updateCookie(interestedIntersection.toString(), {
            domain: isLocalhost ? window.location.hostname : `reveliolabs.com`,
            expires: 30,
          });
        }
        // send new trial user and interested selections to crm. No userId —
        // the appointment flow creates a CRM lead only, never a user, so
        // there is no metadata to stamp.
        await axios.post(
          `${environment.PUBLIC_USER_MGMT_URL}/api/users/interested-trial`,
          {
            email: formData.email,
            interests: [interest],
            company: organization.trim(),
          },
          {
            timeout: 0,
            headers: {
              'x-surf-token': csrfToken,
            },
            withCredentials: true,
            xsrfCookieName: 'x-surf-token',
            xsrfHeaderName: 'x-surf-token',
          }
        );
        if (!isLocalhost && !isE2eTestSignupEmail(formData.email)) {
          mixpanel.track(CTAInteractionTrackingEvents.PROVIDE_ONBOARDING_INFO, {
            source: 'dashboard',
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
    // The appointment flow has no dashboard to reroute to — always finish on
    // the thank-you step.
    setActiveStep(3);
  }, [
    csrfToken,
    updateCookie,
    formData.email,
    interest,
    organization,
    setActiveStep,
  ]);

  return (
    <Stack gap="var(--ap-code-gap)" fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Tell us a little about yourself</ThemeHeading>
        <ThemeTagline>
          Help us personalize your experience and recommend relevant workforce
          insights.
        </ThemeTagline>
      </Stack>
      <AboutYouFields
        organization={organization}
        onOrganizationChange={setOrganization}
        interest={interest}
        onInterestChange={setInterest}
        onContinue={handleOnboarding}
        loading={loading}
        disabled={!formData.email}
      />
    </Stack>
  );
}

export default Step2A;
